import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "../../utils/api";
import { toast } from "../ui/sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import TiptapEditor from "../../pages/services/TiptapEditor";
import {
  AlertTriangle,
  Copy,
  Loader2,
  NotebookPen,
  Pin,
  PinOff,
  Plus,
  Save,
  Search,
  Trash2,
  X,
} from "lucide-react";
import { accentBg } from "../../utils/accent";
import QuickCanvasModal from "./QuickCanvasModal";
import RailHeader from "./RailHeader";

const dataHoraBR = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return `${d.toLocaleDateString("pt-BR")} · ${d.toLocaleTimeString("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
  })}`;
};

const rotulo = (n) =>
  n.title?.trim() || (n.content_text || "").split(/[.!?\n]/)[0].slice(0, 60) || "Sem título";

const VAZIA = { title: "", content_html: "" };

/** Notas em modal canvas: trilha com busca/lista, folha de escrita à direita. */
export default function QuickNotesModal({ open, onClose }) {
  const [itens, setItens] = useState([]);
  const [busca, setBusca] = useState("");
  const [carregando, setCarregando] = useState(true);
  const [nota, setNota] = useState(VAZIA); // { id?, title, content_html }
  const [salvando, setSalvando] = useState(false);
  const [sujo, setSujo] = useState(false);
  const [confirmaDescarte, setConfirmaDescarte] = useState(null); // ação pendente
  const [confirmaExclusao, setConfirmaExclusao] = useState(null);
  const tituloRef = useRef(null);

  const buscar = async (q = "") => {
    try {
      const { data } = await api.get("/notes", { params: q ? { q } : {} });
      setItens(Array.isArray(data) ? data : []);
    } catch {
      setItens([]);
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    setCarregando(true);
    setNota(VAZIA);
    setSujo(false);
    setBusca("");
    buscar("");
    setTimeout(() => tituloRef.current?.focus(), 120);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => buscar(busca), 350);
    return () => clearTimeout(t);
  }, [busca, open]);

  const trocar = (acao) => {
    if (sujo) return setConfirmaDescarte(() => acao);
    acao();
  };

  const abrirNota = (n) =>
    trocar(() => {
      setSujo(false);
      setNota({ id: n.id, title: n.title || "", content_html: n.content_html || "" });
    });

  const nova = () =>
    trocar(() => {
      setSujo(false);
      setNota(VAZIA);
      setTimeout(() => tituloRef.current?.focus(), 60);
    });

  const salvar = async () => {
    const vazia = !nota.title.trim() && !nota.content_html.replace(/<[^>]*>/g, "").trim();
    if (vazia) return toast.error("Escreva algo antes de salvar.");
    setSalvando(true);
    try {
      const payload = { title: nota.title, content_html: nota.content_html };
      const { data } = nota.id
        ? await api.patch(`/notes/${nota.id}`, payload)
        : await api.post("/notes", payload);
      setItens((l) => (nota.id ? l.map((n) => (n.id === data.id ? data : n)) : [data, ...l]));
      setNota({ id: data.id, title: data.title || "", content_html: data.content_html || "" });
      setSujo(false);
      toast.success(nota.id ? "Nota atualizada." : "Nota salva.");
    } catch (err) {
      toast.error(formatApiError(err, "Não foi possível salvar a nota."));
    } finally {
      setSalvando(false);
    }
  };

  const excluir = async () => {
    const alvo = confirmaExclusao;
    setConfirmaExclusao(null);
    setItens((l) => l.filter((n) => n.id !== alvo.id));
    if (nota.id === alvo.id) {
      setNota(VAZIA);
      setSujo(false);
    }
    try {
      await api.delete(`/notes/${alvo.id}`);
      toast.success("Nota excluída.");
    } catch (err) {
      toast.error(formatApiError(err, "Não foi possível excluir."));
      buscar(busca);
    }
  };

  const fixar = async (n) => {
    setItens((l) => l.map((x) => (x.id === n.id ? { ...x, pinned: !n.pinned } : x)));
    try {
      await api.patch(`/notes/${n.id}`, { pinned: !n.pinned });
    } finally {
      buscar(busca);
    }
  };

  const copiar = async (n) => {
    try {
      await navigator.clipboard.writeText(n.content_text || "");
      toast.success("Conteúdo copiado.");
    } catch {
      toast.error("Não foi possível copiar.");
    }
  };

  const tentarFechar = () => trocar(onClose);

  const rail = (
    <>
      <RailHeader label="Minhas notas" />
      <div className="px-3 pb-2">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Buscar nas notas..."
            className="h-9 pl-9 rounded-lg bg-white"
            data-testid="quick-note-search"
          />
        </div>
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto px-3 pb-3 space-y-1">
        {carregando ? (
          <div className="flex items-center justify-center h-24">
            <Loader2 className="h-4 w-4 animate-spin text-gray-300" />
          </div>
        ) : !itens.length ? (
          <div className="text-center py-10 text-gray-400">
            <NotebookPen className="h-8 w-8 mx-auto mb-2 opacity-40" />
            <p className="text-xs font-semibold">
              {busca ? "Nada encontrado" : "Nenhuma nota ainda"}
            </p>
          </div>
        ) : (
          itens.map((n) => {
            const ativa = nota.id === n.id;
            return (
              <div
                key={n.id}
                className={`group rounded-lg border transition-colors ${
                  ativa
                    ? "bg-white border-gray-200 shadow-sm"
                    : "border-transparent hover:bg-gray-100/60"
                }`}
                data-testid={`quick-note-${n.id}`}
              >
                <button
                  type="button"
                  onClick={() => abrirNota(n)}
                  className="w-full text-left px-3 pt-2 pb-1"
                  data-testid={`quick-note-open-${n.id}`}
                >
                  <div className="flex items-center gap-1.5">
                    {n.pinned && <Pin size={11} className="shrink-0 text-gray-400" />}
                    <p
                      className={`text-xs truncate ${
                        ativa ? "text-gray-900 font-semibold" : "text-gray-600 font-medium"
                      }`}
                    >
                      {rotulo(n)}
                    </p>
                  </div>
                  {n.content_text && (
                    <p className="text-[11px] text-gray-400 mt-0.5 line-clamp-1">
                      {n.content_text}
                    </p>
                  )}
                  <p className="text-[10px] text-gray-300 mt-1">{dataHoraBR(n.updated_at)}</p>
                </button>
                <div className="flex items-center gap-0.5 px-2 pb-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    type="button"
                    onClick={() => fixar(n)}
                    title={n.pinned ? "Desafixar" : "Fixar"}
                    className="h-6 w-6 rounded-md flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-200/70"
                    data-testid={`quick-note-pin-${n.id}`}
                  >
                    {n.pinned ? <PinOff size={12} /> : <Pin size={12} />}
                  </button>
                  <button
                    type="button"
                    onClick={() => copiar(n)}
                    title="Copiar conteúdo"
                    className="h-6 w-6 rounded-md flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-200/70"
                    data-testid={`quick-note-copy-${n.id}`}
                  >
                    <Copy size={12} />
                  </button>
                  <div className="flex-1" />
                  <button
                    type="button"
                    onClick={() => setConfirmaExclusao(n)}
                    title="Excluir nota"
                    className="h-6 w-6 rounded-md flex items-center justify-center text-gray-400 hover:text-red-600 hover:bg-red-50"
                    data-testid={`quick-note-delete-${n.id}`}
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>
      <div className="px-3 py-3 border-t border-gray-200">
        <Button
          type="button"
          onClick={nova}
          className="w-full h-9 text-white gap-1.5"
          style={accentBg()}
          data-testid="quick-note-add"
        >
          <Plus size={15} /> Nova nota
        </Button>
      </div>
    </>
  );

  return (
    <>
      <QuickCanvasModal
        open={open}
        onClose={tentarFechar}
        title="Notas"
        subtitle="Anotações privadas"
        icon={NotebookPen}
        rail={rail}
        testId="quick-notes-modal"
        onEscapeKeyDown={(e) => {
          if (sujo) {
            e.preventDefault();
            setConfirmaDescarte(() => onClose);
          }
        }}
        onInteractOutside={(e) => {
          if (sujo) e.preventDefault();
        }}
      >
        <div className="px-6 pt-5 pb-4 shrink-0 border-b border-gray-100">
          <label className="text-sm font-medium mb-2 block text-gray-700">Título</label>
          <div className="flex items-center gap-3">
            <Input
              ref={tituloRef}
              value={nota.title}
              onChange={(e) => {
                setSujo(true);
                setNota((n) => ({ ...n, title: e.target.value }));
              }}
              placeholder="Título da nota"
              className="rounded-lg h-9"
              data-testid="quick-note-title"
            />
            <span className="shrink-0 inline-flex items-center gap-1.5 text-xs text-gray-500 whitespace-nowrap">
              <span
                className="h-1.5 w-1.5 rounded-full"
                style={{
                  backgroundColor: sujo ? "#f59e0b" : nota.id ? "#10b981" : "#d1d5db",
                }}
              />
              {sujo ? "Não salvo" : nota.id ? "Salva" : "Rascunho"}
            </span>
          </div>
        </div>

        <div className="flex-1 min-h-0 px-6 py-4">
          <TiptapEditor
            key={nota.id || "nova"}
            value={nota.content_html}
            onChange={(html) => {
              setSujo(true);
              setNota((n) => ({ ...n, content_html: html }));
            }}
            placeholder="Escreva sua nota..."
            minHeight={160}
            fill
            testIdPrefix="quick-note-editor"
          />
        </div>

        <div className="flex items-center gap-3 px-6 py-4 border-t border-gray-100 shrink-0 bg-white">
          <p className="text-xs text-gray-500 flex-1 min-w-0 truncate">
            {nota.id ? `Editando · ${rotulo(nota)}` : "Nova nota"}
          </p>
          <Button
            type="button"
            variant="outline"
            onClick={tentarFechar}
            className="h-9 gap-1.5"
            data-testid="quick-note-cancel"
          >
            <X size={15} /> Cancelar
          </Button>
          <Button
            type="button"
            onClick={salvar}
            disabled={salvando}
            className="h-9 text-white gap-1.5"
            style={accentBg()}
            data-testid="quick-note-save"
          >
            {salvando ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save size={15} />}
            Salvar
          </Button>
        </div>
      </QuickCanvasModal>

      <AlertDialog open={!!confirmaDescarte} onOpenChange={(v) => !v && setConfirmaDescarte(null)}>
        <AlertDialogContent
          className="z-[1800]"
          overlayClassName="z-[1799]"
          data-testid="quick-note-discard-dialog"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              Descartar alterações?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Você tem alterações que ainda não foram salvas nesta nota. Elas serão perdidas.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Continuar editando</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={() => {
                const acao = confirmaDescarte;
                setConfirmaDescarte(null);
                setSujo(false);
                acao?.();
              }}
              data-testid="quick-note-discard-confirm"
            >
              Descartar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!confirmaExclusao} onOpenChange={(v) => !v && setConfirmaExclusao(null)}>
        <AlertDialogContent
          className="z-[1800]"
          overlayClassName="z-[1799]"
          data-testid="quick-note-delete-dialog"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              Excluir nota?
            </AlertDialogTitle>
            <AlertDialogDescription>
              A nota <strong>{confirmaExclusao ? rotulo(confirmaExclusao) : ""}</strong> será
              excluída. Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={excluir}
              data-testid="quick-note-delete-confirm"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
